import { APIRoutes } from '@/services/api.routes';
import { calculateDayTrade } from '@/utils/daytrade-calculations';
import { SESSION_BUDGET } from '@/utils/daytrade-session-profit';
import { formatDate, formatDateOnly } from '@/utils/helpers';
import type { DayTrade, DayTradingSession } from '@/models/day-trading-session';
import type { DayTradeState } from '@/models/enums';
import type { DayTradeID } from '@/models/ids';

const FETCH_PAGE_SIZE = 500;

const STATE_LABELS: Record<DayTradeState, string> = {
  OPEN: 'Offen',
  GOAL_REACHED: 'Ziel erreicht',
  STOPPED: 'Gestoppt',
  STOPLOSS: 'Stop-Loss',
};

interface TradeOccurrence {
  session: DayTradingSession;
  trade: DayTrade;
}

interface TradeGroup {
  occurrences: TradeOccurrence[];
}

async function fetchAllSessions(): Promise<DayTradingSession[]> {
  const sessions: DayTradingSession[] = [];
  for (let page = 1; ; page++) {
    const response = await APIRoutes.listDayTradingSessions({
      limit: FETCH_PAGE_SIZE,
      next: page > 1 ? `$${page}` : undefined,
      sort: 'start',
      sortDirection: 'ASC',
    });
    sessions.push(...response.sessions);
    if (response.sessions.length === 0 || sessions.length >= response.total) break;
  }
  return sessions;
}

function isSameCarriedOverTrade(a: DayTrade, b: DayTrade): boolean {
  return a.symbol === b.symbol && a.type === b.type && a.created === b.created;
}

// A trade carried over into a later session shows up once per session; merge those into one group.
// Sessions must be sorted by start ascending.
function groupTrades(sessions: DayTradingSession[]): TradeGroup[] {
  const groups: TradeGroup[] = [];
  const groupsByTradeId = new Map<DayTradeID, TradeGroup>();

  for (const session of sessions) {
    for (const trade of session.state.trades ?? []) {
      let group = groupsByTradeId.get(trade.id);

      if (group === undefined && trade.migratedFrom !== null) {
        group = groups.find((g) => {
          const last = g.occurrences[g.occurrences.length - 1];
          return last.session.id === trade.migratedFrom && isSameCarriedOverTrade(last.trade, trade);
        });
      }

      if (group === undefined) {
        group = { occurrences: [] };
        groups.push(group);
      }
      groupsByTradeId.set(trade.id, group);

      const last = group.occurrences.length > 0 ? group.occurrences[group.occurrences.length - 1] : null;
      if (last !== null && last.session.id === session.id) {
        last.trade = trade;
      } else {
        group.occurrences.push({ session, trade });
      }
    }
  }

  return groups;
}

function num(n: number, decimals = 2): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: false });
}

function shares(n: number): string {
  return parseFloat(n.toFixed(4)).toLocaleString('de-DE', { maximumFractionDigits: 4, useGrouping: false });
}

function csvCell(value: string): string {
  if (/[";\r\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

const HEADER = [
  'ID',
  'Symbol',
  'Typ',
  'Status',
  'Eröffnungs-Session',
  'Schluss-Session',
  'Anzahl Sessions',
  'Eröffnet am',
  'Zuletzt geändert',
  'Anteil pro Einstieg',
  'Betrag pro Einstieg ($)',
  'Anzahl Einstiege',
  'Ausgeführte Einstiege',
  'Einstiegskurse ($)',
  'Aktien gesamt',
  'Ø Einstieg ($)',
  'Investiert ($)',
  'Ziel (+/- $)',
  'Zielkurs ($)',
  'Kurs → Ziel',
  'Ausstieg ($)',
  'Theoret. Gewinn ($)',
  'Theoret. Gewinn (% vom Einsatz)',
  'Ergebnis Session-Liste ($)',
  'Ergebnis Session-Liste (% vom Depot)',
  'Session-IDs',
];

function buildRow(group: TradeGroup): string[] {
  const first = group.occurrences[0];
  const last = group.occurrences[group.occurrences.length - 1];
  const trade = last.trade;
  const budgetCents = SESSION_BUDGET * 100;

  // Same mode as the trade cards, so open trades show their target-based profit.
  const calc = calculateDayTrade({
    trade: trade,
    budget: budgetCents,
    mode: 'moderator',
    processedAdjustements: [],
    skippedAdjustements: [],
    overrideAdjustementPrices: {},
    overrideAdjustmentStockCount: {},
    stockCountMode: 'NORMAL',
  });

  // Same rules as calculateSessionProfit: only GOAL_REACHED/STOPLOSS count towards the list result.
  const listProfit = (trade.state === 'GOAL_REACHED' || trade.state === 'STOPLOSS')
    ? calculateDayTrade({
        trade: trade,
        budget: budgetCents,
        mode: 'summary',
        processedAdjustements: [],
        skippedAdjustements: [],
        overrideAdjustementPrices: {},
        overrideAdjustmentStockCount: {},
        stockCountMode: 'NORMAL',
      }).profit
    : 0;

  const hasEntries = trade.adjustments.length > 0;

  return [
    trade.id,
    trade.symbol,
    trade.type === 'LONG' ? 'Long' : 'Short',
    STATE_LABELS[trade.state],
    formatDateOnly(first.session.start),
    trade.state === 'OPEN' ? '' : formatDateOnly(last.session.start),
    String(group.occurrences.length),
    formatDate(trade.created),
    formatDate(trade.updated),
    trade.share > 0 ? `1/${Math.round(1 / trade.share)}` : '',
    num(calc.perEntry),
    String(trade.adjustments.length),
    String(trade.adjustments.filter((a) => a.executed).length),
    trade.adjustments.map((a) => num(a.entryPrice / 100)).join(' | '),
    shares(calc.totalShares),
    hasEntries ? num(calc.avgPrice) : '',
    num(calc.totalInvested),
    num(calc.targetPriceDelta),
    hasEntries ? num(calc.targetPriceAbsolute) : '',
    hasEntries ? `Ø $${num(calc.avgPrice)} → $${num(calc.targetPriceDelta)} = $${num(calc.targetPriceAbsolute)}` : '',
    trade.exitPrice !== null ? num(trade.exitPrice / 100) : '',
    num(calc.profit),
    num(calc.profitPct),
    num(listProfit),
    num((listProfit / SESSION_BUDGET) * 100, 4),
    group.occurrences.map((o) => o.session.id).join(' | '),
  ];
}

export async function buildDayTradesCsv(): Promise<string> {
  const sessions = await fetchAllSessions();
  const groups = groupTrades(sessions);

  const lines = [HEADER, ...groups.map(buildRow)].map((row) => row.map(csvCell).join(';'));
  // BOM so Excel detects UTF-8 (umlauts, "Ø", "→")
  return '\uFEFF' + lines.join('\r\n') + '\r\n';
}

export async function downloadDayTradesCsv(): Promise<void> {
  const csv = await buildDayTradesCsv();
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `daytrading-trades_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
